import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useAiManagement } from "@/hooks/use-ai-management";
import { Globe, RefreshCw, CheckCircle, AlertCircle } from "lucide-react";

export const MultiLanguageManagement: React.FC = () => {
  const { toast } = useToast();
  const { convertToMultiLanguage, convertBulkToMultiLanguage } = useAiManagement();
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("vi");
  const [selectedTopic, setSelectedTopic] = useState<string>("guide");
  const [isConverting, setIsConverting] = useState(false);
  const [stats, setStats] = useState({
    totalDocuments: 0,
    multiLanguageDocs: 0,
    singleLanguageDocs: 0,
    byLanguage: {} as Record<string, number>
  });

  // Fetch documents
  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('assistant_knowledge')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setDocuments(data || []);
      
      // Calculate stats
      const totalCount = data?.length || 0;
      const multiCount = data?.filter(doc => doc.metadata?.is_multi_language === true).length || 0;
      
      // Count by language
      const langCounts: Record<string, number> = {};
      data?.forEach(doc => {
        const lang = doc.metadata?.language || 'unknown';
        langCounts[lang] = (langCounts[lang] || 0) + 1;
      });
      
      setStats({
        totalDocuments: totalCount,
        multiLanguageDocs: multiCount,
        singleLanguageDocs: totalCount - multiCount,
        byLanguage: langCounts
      });
      
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast({
        title: "Error",
        description: "Failed to fetch documents",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  // Handle single document conversion
  const handleConvertDocument = async (docId: string) => {
    try {
      const result = await convertToMultiLanguage(docId, selectedLanguage);
      if (result) {
        // Refresh the document list
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error converting document:', error);
      toast({
        title: "Error",
        description: "Failed to convert document",
        variant: "destructive"
      });
    }
  };

  // Handle bulk conversion
  const handleBulkConvert = async () => {
    setIsConverting(true);
    try {
      const result = await convertBulkToMultiLanguage({
        language: selectedTopic ? undefined : selectedLanguage,
        topic: selectedTopic || undefined,
        sourceLanguage: selectedLanguage
      });
      
      toast({
        title: "Conversion Complete",
        description: `Successfully converted ${result.success} documents, ${result.failed} failed`,
        variant: result.failed > 0 ? "default" : "default"
      });
      
      // Refresh the document list
      fetchDocuments();
      
    } catch (error) {
      console.error('Error in bulk conversion:', error);
      toast({
        title: "Error",
        description: "Bulk conversion failed",
        variant: "destructive"
      });
    } finally {
      setIsConverting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Multi-Language Support Management
          </CardTitle>
          <CardDescription>
            Configure and manage multi-language knowledge documents
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="stats">
            <TabsList>
              <TabsTrigger value="stats">Statistics</TabsTrigger>
              <TabsTrigger value="convert">Convert Documents</TabsTrigger>
              <TabsTrigger value="documents">Document List</TabsTrigger>
            </TabsList>
            
            <TabsContent value="stats" className="space-y-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="py-4">
                    <CardTitle className="text-sm font-medium">Total Documents</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{stats.totalDocuments}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="py-4">
                    <CardTitle className="text-sm font-medium">Multi-Language Docs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-green-500">{stats.multiLanguageDocs}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="py-4">
                    <CardTitle className="text-sm font-medium">Single-Language Docs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-amber-500">{stats.singleLanguageDocs}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="py-4">
                    <CardTitle className="text-sm font-medium">Coverage Rate</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      {stats.totalDocuments > 0 
                        ? Math.round((stats.multiLanguageDocs / stats.totalDocuments) * 100) 
                        : 0}%
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              <Card>
                <CardHeader>
                  <CardTitle>Documents by Language</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {Object.entries(stats.byLanguage).map(([lang, count]) => (
                      <div key={lang} className="flex items-center justify-between border rounded-md p-3">
                        <span className="font-medium uppercase">{lang}</span>
                        <span className="text-muted-foreground">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="convert" className="space-y-4 pt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Bulk Document Conversion</CardTitle>
                  <CardDescription>
                    Convert multiple documents to multi-language format at once
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="language">Source Language</Label>
                      <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                        <SelectTrigger id="language">
                          <SelectValue placeholder="Select language" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vi">Vietnamese (vi)</SelectItem>
                          <SelectItem value="en">English (en)</SelectItem>
                          <SelectItem value="zh">Chinese (zh)</SelectItem>
                          <SelectItem value="ko">Korean (ko)</SelectItem>
                          <SelectItem value="ja">Japanese (ja)</SelectItem>
                          <SelectItem value="th">Thai (th)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="topic">Filter by Topic (Optional)</Label>
                      <Select value={selectedTopic} onValueChange={setSelectedTopic}>
                        <SelectTrigger id="topic">
                          <SelectValue placeholder="All topics" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="guide">All topics</SelectItem>
                          <SelectItem value="guide">Guide</SelectItem>
                          <SelectItem value="performance">Performance</SelectItem>
                          <SelectItem value="trading_strategy">Trading Strategy</SelectItem>
                          <SelectItem value="advisor">Advisor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button 
                    onClick={handleBulkConvert}
                    disabled={isConverting}
                    className="w-full"
                  >
                    {isConverting ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Converting...
                      </>
                    ) : (
                      <>
                        <Globe className="mr-2 h-4 w-4" />
                        Convert Documents to Multi-Language
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            </TabsContent>
            
            <TabsContent value="documents" className="pt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">Document List</h3>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchDocuments}
                  disabled={loading}
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              
              <Table>
                <TableCaption>All available knowledge documents</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Language</TableHead>
                    <TableHead>Topic</TableHead>
                    <TableHead>Multi-Language</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map(doc => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-medium truncate max-w-[200px]">
                        {(doc.document?.title || doc.metadata?.title || 'Untitled')}
                      </TableCell>
                      <TableCell className="uppercase">{doc.metadata?.language || 'unknown'}</TableCell>
                      <TableCell>{doc.metadata?.topic || 'unknown'}</TableCell>
                      <TableCell>
                        {doc.metadata?.is_multi_language ? (
                          <span className="flex items-center text-green-500">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Yes
                          </span>
                        ) : (
                          <span className="flex items-center text-amber-500">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            No
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleConvertDocument(doc.id)}
                          disabled={doc.metadata?.is_multi_language === true}
                        >
                          <Globe className="h-4 w-4 mr-1" />
                          Convert
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {documents.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No documents found
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        <div className="flex items-center justify-center">
                          <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                          Loading documents...
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
